import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, XCircle, Clock, RotateCcw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SyndicationWithdrawals = ({ embedded = false }) => {
  // 11/03 (Josh: consolidate pages): `embedded` renders the body without the app shell, inside a hub tab.
  const Wrap = embedded ? React.Fragment : Layout;
  const { getAuthHeaders } = useAuth();
  const { tenant } = useTenant();
  const [withdrawals, setWithdrawals] = useState([]);
  const [selectedWithdrawals, setSelectedWithdrawals] = useState({});
  const [loading, setLoading] = useState(true);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetSyndicator, setResetSyndicator] = useState(null);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetReason, setResetReason] = useState('');
  const [resetting, setResetting] = useState(false);

  const fetchWithdrawals = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/withdrawals`, {
        headers: getAuthHeaders(),
      });
      setWithdrawals(response.data);
    } catch (error) {
      console.error('Failed to fetch withdrawals:', error);
      toast.error('Failed to load withdrawals');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchWithdrawals();
  }, [fetchWithdrawals]);

  const handleSelectWithdrawal = (withdrawalId, checked) => {
    setSelectedWithdrawals({
      ...selectedWithdrawals,
      [withdrawalId]: checked
    });
  };

  const handleSelectAll = (checked) => {
    const newSelection = {};
    withdrawals.filter(w => w.status === 'Pending').forEach(w => {
      newSelection[w.id] = checked;
    });
    setSelectedWithdrawals(newSelection);
  };

  const handleApprove = async (withdrawalId) => {
    try {
      await axios.post(
        `${API}/withdrawals/${withdrawalId}/approve`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Withdrawal approved successfully');
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve withdrawal');
    }
  };

  const handleReject = async (withdrawalId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await axios.post(
        `${API}/withdrawals/${withdrawalId}/reject`,
        { reason },
        { headers: getAuthHeaders() }
      );
      toast.success('Withdrawal rejected');
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject withdrawal');
    }
  };

  const handleBulkApprove = async () => {
    const selectedIds = Object.keys(selectedWithdrawals).filter(id => selectedWithdrawals[id]);
    
    if (selectedIds.length === 0) {
      toast.error('No withdrawals selected');
      return;
    }

    if (selectedIds.length > 15) {
      toast.error('You can only approve up to 15 withdrawals at once');
      return;
    }

    try {
      for (const id of selectedIds) {
        await axios.post(
          `${API}/withdrawals/${id}/approve`,
          {},
          { headers: getAuthHeaders() }
        );
      }
      toast.success(`${selectedIds.length} withdrawals approved`);
      setSelectedWithdrawals({});
      fetchWithdrawals();
    } catch (error) {
      toast.error('Failed to approve some withdrawals');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      Pending: <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />{status}</Badge>,
      Approved: <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />{status}</Badge>,
      Rejected: <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />{status}</Badge>,
      Undone: <Badge className="bg-gray-100 text-gray-800"><RotateCcw className="h-3 w-3 mr-1" />{status}</Badge>
    };
    return variants[status] || <Badge>{status}</Badge>;
  };

  // Get unique syndicators for reset feature
  const uniqueSyndicators = [...new Map(
    withdrawals
      .filter(w => w.syndicator_id && w.syndicator_name)
      .map(w => [w.syndicator_id, { id: w.syndicator_id, name: w.syndicator_name }])
  ).values()];

  const handleResetWithdrawals = async () => {
    if (resetConfirmText !== 'RESET') {
      toast.error('Please type RESET to confirm');
      return;
    }
    
    setResetting(true);
    try {
      const response = await axios.post(
        `${API}/withdrawals/syndicator/${resetSyndicator.id}/reset-withdrawals`,
        null,
        { 
          headers: { 
            ...getAuthHeaders(),
            'X-Tenant': tenant || 'live'
          },
          params: { reason: resetReason || undefined }
        }
      );
      
      toast.success(`Reset withdrawals for ${resetSyndicator.name}: ${formatCurrency(response.data.total_amount_reset)} across ${response.data.syndications_reset} syndications`);
      setResetDialog(false);
      setResetSyndicator(null);
      setResetConfirmText('');
      setResetReason('');
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset withdrawals');
    } finally {
      setResetting(false);
    }
  };

  const selectedCount = Object.values(selectedWithdrawals).filter(Boolean).length;
  const pendingWithdrawals = withdrawals.filter(w => w.status === 'Pending');

  return (
    <Wrap>
      <div className="px-4 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Syndication Withdrawals</h1>
          <div className="flex space-x-2">
            {selectedCount > 0 && (
              <Button onClick={handleBulkApprove}>
                Withdraw (up to 15) - Selected: {selectedCount}
              </Button>
            )}
          </div>
        </div>

        {/* Reset Withdrawals Section */}
        {uniqueSyndicators.length > 0 && (
          <Card className="mb-4">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Reset Withdrawals to Zero</h3>
                  <p className="text-sm text-gray-500">Select a syndicator to reset their withdrawal totals</p>
                </div>
                <div className="flex items-center gap-2">
                  <select 
                    className="border rounded-md px-3 py-2 text-sm"
                    onChange={(e) => {
                      const syn = uniqueSyndicators.find(s => s.id === e.target.value);
                      if (syn) {
                        setResetSyndicator(syn);
                        setResetDialog(true);
                      }
                    }}
                    value=""
                  >
                    <option value="">Select syndicator...</option>
                    {uniqueSyndicators.map(syn => (
                      <option key={syn.id} value={syn.id}>{syn.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : withdrawals.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No withdrawal requests found.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-100">
                    <TableHead>
                      <Checkbox
                        checked={pendingWithdrawals.length > 0 && pendingWithdrawals.every(w => selectedWithdrawals[w.id])}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>Syndicator</TableHead>
                    <TableHead>Deal</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Request Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Approved By</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {withdrawals.map((withdrawal) => (
                    <TableRow 
                      key={withdrawal.id} 
                      className={`hover:bg-gray-50 ${withdrawal.exceeds_available && withdrawal.status === 'Pending' ? 'bg-red-50' : ''}`}
                    >
                      <TableCell>
                        {withdrawal.status === 'Pending' && (
                          <Checkbox
                            checked={selectedWithdrawals[withdrawal.id] || false}
                            onCheckedChange={(checked) => handleSelectWithdrawal(withdrawal.id, checked)}
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-blue-600">
                        {withdrawal.syndicator_name}
                      </TableCell>
                      <TableCell className="text-blue-600">
                        {withdrawal.deal_name || 'All Deals'}
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold">
                          {formatCurrency(withdrawal.amount)}
                        </div>
                        {withdrawal.status === 'Pending' && (
                          <div className="text-xs text-gray-500">
                            Available: {formatCurrency(withdrawal.available_balance || 0)}
                          </div>
                        )}
                        {withdrawal.exceeds_available && withdrawal.status === 'Pending' && (
                          <Badge className="bg-red-500 text-white text-xs mt-1">
                            ⚠️ Exceeds Available
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(withdrawal.request_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{getStatusBadge(withdrawal.status)}</TableCell>
                      <TableCell>{withdrawal.approved_by_name || '-'}</TableCell>
                      <TableCell className="max-w-xs truncate">{withdrawal.notes || '-'}</TableCell>
                      <TableCell className="text-right">
                        {withdrawal.status === 'Pending' && (
                          <div className="flex justify-end space-x-2">
                            <Button
                              size="sm"
                              variant={withdrawal.exceeds_available ? "destructive" : "outline"}
                              onClick={() => {
                                if (withdrawal.exceeds_available) {
                                  const proceed = window.confirm(
                                    `WARNING: This withdrawal of ${formatCurrency(withdrawal.amount)} exceeds the available balance of ${formatCurrency(withdrawal.available_balance || 0)}.\n\nDo you still want to approve?`
                                  );
                                  if (proceed) handleApprove(withdrawal.id);
                                } else {
                                  handleApprove(withdrawal.id);
                                }
                              }}
                            >
                              {withdrawal.exceeds_available ? '⚠️ Approve' : 'Approve'}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReject(withdrawal.id)}
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Reset Confirmation Dialog */}
        <Dialog open={resetDialog} onOpenChange={setResetDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Reset Withdrawals to Zero
              </DialogTitle>
              <DialogDescription>
                This will reset all withdrawal totals for <strong>{resetSyndicator?.name}</strong> to $0.
                Their available balance will recalculate based on running collection.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="reset-confirm">Type <strong>RESET</strong> to confirm</Label>
                <Input
                  id="reset-confirm"
                  value={resetConfirmText}
                  onChange={(e) => setResetConfirmText(e.target.value)}
                  placeholder="RESET"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="reset-reason">Reason (optional)</Label>
                <Textarea
                  id="reset-reason"
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  placeholder="Enter reason for reset..."
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setResetDialog(false);
                setResetConfirmText('');
                setResetReason('');
              }}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleResetWithdrawals}
                disabled={resetConfirmText !== 'RESET' || resetting}
              >
                {resetting ? 'Resetting...' : 'Reset to $0'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Wrap>
  );
};

export default SyndicationWithdrawals;